import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { useEstablishmentTechnicalProfessionals } from '@/common/hooks/queries/useEstablishmentTechnicalProfessionals'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import {
  EstablishmentTechnicalProfessionalData,
  EstablishmentTechnicalProfessionalTableItem,
} from '@/common/types/establishmentTechnicalProfessional'
import { cn } from '@/common/utils/cn'
import { defaultColumns } from '@/features/maintenance/establishmentTechnicalProfessionals/constants/columns.constant'
import { EstablishmentTechnicalProfessionalsTransformer } from '@/features/maintenance/establishmentTechnicalProfessionals/services/establishmentTechnicalProfessionalsTransformer'
import { BookUserIcon, SearchIcon } from 'lucide-react'

const List = () => {
  const { filterParams, setFilterParams } = useFilterParams()

  const [search, setSearch] = useDebounce(filterParams.search || '')

  const {
    data: establishmentTechnicalProfessionals,
    meta,
    isPending: establishmentTechnicalProfessionalsIsPending,
    error: establishmentTechnicalProfessionalsError = {} as any,
    refetch: refetchEstablishmentTechnicalProfessionals,
  } = useEstablishmentTechnicalProfessionals({
    filters: {
      search,
      page: filterParams.page || 1,
    },
    mapper: Mapper.mapWithTransformer<
      EstablishmentTechnicalProfessionalData[],
      EstablishmentTechnicalProfessionalTableItem[]
    >({
      transformer: EstablishmentTechnicalProfessionalsTransformer.tableTransformer(),
    }),
  })

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<BookUserIcon className="size-10" strokeWidth={1} />}
        title="Especialidades Técnicas"
        createButtonText="Nova Especialidade"
        dataTestid="create_establishment_technical_professional_button"
        operationKlassToCheckPermission="Admin::Maintenance::EstablishmentTechnicalProfessionals::Create"
        disableCreateButton={
          establishmentTechnicalProfessionalsIsPending || establishmentTechnicalProfessionalsError?.status === 403
        }
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={establishmentTechnicalProfessionalsError?.status}
        refetchFunction={refetchEstablishmentTechnicalProfessionals}
      >
        <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value)
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
              }}
              value={filterParams.search}
            />
          </div>

          <Table
            className={cn('max-h-screen', {
              'shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]': !!establishmentTechnicalProfessionals?.length,
            })}
            columns={defaultColumns}
            isLoadingData={establishmentTechnicalProfessionalsIsPending}
            page={filterParams.page || 1}
            rows={establishmentTechnicalProfessionals}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default List
